import { useEffect, useRef, useState } from "react";
import { Video, VideoOff, Loader2, Users, Radio } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const JITSI_DOMAIN = process.env.REACT_APP_JITSI_DOMAIN || "meet.jit.si";
let jitsiScriptPromise = null;

function loadJitsiScript() {
  if (window.JitsiMeetExternalAPI) return Promise.resolve();
  if (jitsiScriptPromise) return jitsiScriptPromise;
  jitsiScriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = `https://${JITSI_DOMAIN}/external_api.js`;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => {
      jitsiScriptPromise = null;
      reject(new Error("script"));
    };
    document.body.appendChild(s);
  });
  return jitsiScriptPromise;
}

function JitsiRoom({ room, displayName }) {
  const ref = useRef(null);
  const apiRef = useRef(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let disposed = false;
    loadJitsiScript()
      .then(() => {
        if (disposed || !ref.current || !window.JitsiMeetExternalAPI) return;
        apiRef.current = new window.JitsiMeetExternalAPI(JITSI_DOMAIN, {
          roomName: room,
          parentNode: ref.current,
          width: "100%",
          height: "100%",
          userInfo: { displayName: displayName || "Deltaker" },
          configOverwrite: { prejoinPageEnabled: false, startWithAudioMuted: true, disableDeepLinking: true },
          interfaceConfigOverwrite: { MOBILE_APP_PROMO: false, SHOW_JITSI_WATERMARK: false },
        });
      })
      .catch(() => setErr(true));
    return () => {
      disposed = true;
      try {
        apiRef.current?.dispose?.();
      } catch (e) {
        /* ignore */
      }
      apiRef.current = null;
    };
  }, [room, displayName]);

  if (err) {
    return <div className="grid h-full place-items-center text-sm text-muted-foreground">Kunne ikke laste videotjenesten.</div>;
  }
  return <div ref={ref} data-testid="jitsi-container" className="h-full w-full overflow-hidden rounded-xl bg-black" />;
}

export function MeetingVideo({ meetingId, live, refreshLive }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const video = live?.video || { active: false };
  const isChair = !!live?.is_chair;
  const presence = live?.presence || [];

  const act = async (path, okMsg) => {
    setBusy(true);
    try {
      await api.post(`/meetings/${meetingId}/video/${path}`);
      toast.success(okMsg);
      refreshLive?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!live) {
    return (
      <div className="grid h-48 place-items-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="meeting-video">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          {video.active ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 px-2.5 py-1 text-xs font-semibold text-red-600 dark:text-red-400" data-testid="video-live-badge">
              <Radio className="h-3.5 w-3.5 animate-pulse" /> Sending pågår
            </span>
          ) : (
            <span className="text-sm font-medium text-muted-foreground">Videomøtet er ikke startet.</span>
          )}
          {presence.length > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="video-presence" title={presence.map((p) => p.name).join(", ")}>
              <Users className="h-3.5 w-3.5" /> {presence.length} inne nå
            </span>
          ) : null}
        </div>
        {isChair ? (
          video.active ? (
            <Button variant="outline" className="gap-2" disabled={busy} onClick={() => act("end", "Videomøtet er avsluttet")} data-testid="video-end-btn">
              <VideoOff className="h-4 w-4" /> Avslutt videomøte
            </Button>
          ) : (
            <Button className="gap-2 active:scale-[0.98]" disabled={busy} onClick={() => act("start", "Videomøtet er startet")} data-testid="video-start-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Video className="h-4 w-4" />} Start videomøte
            </Button>
          )
        ) : null}
      </div>

      {video.active && video.room ? (
        <div className="h-[70vh] min-h-[420px] overflow-hidden rounded-xl border border-border">
          <JitsiRoom room={video.room} displayName={user?.name} />
        </div>
      ) : (
        <div className="grid h-64 place-items-center rounded-xl border border-dashed border-border text-center" data-testid="video-idle">
          <div className="max-w-sm space-y-2 px-6">
            <Video className="mx-auto h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm font-medium">
              {isChair ? "Trykk «Start videomøte» for å åpne videorommet for alle deltakere." : "Venter på at styreleder starter videomøtet."}
            </p>
            <p className="text-xs text-muted-foreground">Videomøtet kjører direkte i nettleseren – ingen app eller konto nødvendig.</p>
          </div>
        </div>
      )}
    </div>
  );
}
